import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError, API } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import {
  UploadCloud, Download, RefreshCw, ShieldCheck, ShieldAlert, CheckCircle2,
  AlertTriangle, Clock, Rocket, Info, FileJson,
} from "lucide-react";

const COLL_LABELS = { blog_posts: "Blog Posts", blog_redirects: "Redirects", blog_topics: "Topics" };

const StatusPill = ({ tone = "slate", children }) => {
  const map = {
    green: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    red: "bg-red-50 text-red-700 border-red-200",
    slate: "bg-slate-50 text-slate-700 border-slate-200",
    blue: "bg-blue-50 text-blue-700 border-blue-200",
  };
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium ${map[tone]}`}>
      {children}
    </span>
  );
};

function SummaryStat({ label, value, tone }) {
  const map = {
    green: "bg-emerald-50 border-emerald-200 text-emerald-800",
    amber: "bg-amber-50 border-amber-200 text-amber-800",
    red: "bg-red-50 border-red-200 text-red-800",
    blue: "bg-blue-50 border-blue-200 text-blue-800",
    slate: "bg-slate-50 border-slate-200 text-slate-700",
  };
  return (
    <div className={`rounded-lg border p-3 ${map[tone] || map.slate}`}>
      <div className="text-xs uppercase tracking-wide opacity-70">{label}</div>
      <div className="text-2xl font-semibold mt-1">{value}</div>
    </div>
  );
}

export default function AdminContentSync() {
  const [config, setConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const fileRef = useRef(null);

  // Snapshot upload state (production side)
  const [uploadedSnapshot, setUploadedSnapshot] = useState(null); // parsed JSON
  const [uploadedFileName, setUploadedFileName] = useState("");
  const [diff, setDiff] = useState(null);
  const [diffLoading, setDiffLoading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [lastReport, setLastReport] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Download state (preview side)
  const [downloading, setDownloading] = useState(false);

  // Environment detection (browser-side — backend cannot see external host reliably)
  const browserHost = typeof window !== "undefined" ? window.location.hostname.toLowerCase() : "";
  const environment = browserHost.includes("preview.emergentagent")
    ? "PREVIEW"
    : browserHost.includes("instabridgefunding.com")
    ? "PRODUCTION"
    : "UNKNOWN";

  useEffect(() => {
    loadConfig();
    loadHistory();
  }, []);

  const loadConfig = async () => {
    setConfigLoading(true);
    try {
      const { data } = await api.get("/admin/content-sync/config");
      setConfig(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setConfigLoading(false);
    }
  };

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/admin/content-sync/history?limit=10");
      setHistory(data.history || []);
    } catch {
      // best-effort
    }
  };

  // ---- Download Snapshot (preview side) ----
  const downloadSnapshot = async () => {
    setDownloading(true);
    try {
      const { data } = await api.get("/admin/content-sync/download-snapshot");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      a.href = url;
      a.download = `instabridge-cms-snapshot-${ts}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      const c = data.counts || {};
      toast.success(`Snapshot downloaded — ${c.blog_posts || 0} posts, ${c.blog_redirects || 0} redirects, ${c.blog_topics || 0} topics`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDownloading(false);
    }
  };

  // ---- Upload Snapshot (production side) ----
  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploadedFileName(f.name);
    setDiff(null);
    setLastReport(null);
    try {
      const text = await f.text();
      const parsed = JSON.parse(text);
      if (!parsed.signature || !parsed.collections) {
        throw new Error("Not a valid signed CMS snapshot (missing signature or collections)");
      }
      setUploadedSnapshot(parsed);
      toast.success("Snapshot loaded — computing diff…");
      await runDryRun(parsed);
    } catch (err) {
      setUploadedSnapshot(null);
      toast.error(`Could not read snapshot: ${err.message || err}`);
    }
  };

  const runDryRun = async (snapshot) => {
    setDiffLoading(true);
    setDiff(null);
    try {
      const { data } = await api.post("/admin/content-sync/import?dry_run=true", snapshot);
      setDiff(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDiffLoading(false);
    }
  };

  const runPublish = async () => {
    if (!uploadedSnapshot) return;
    setPublishing(true);
    setConfirmOpen(false);
    try {
      const { data } = await api.post("/admin/content-sync/import?dry_run=false", uploadedSnapshot);
      setLastReport(data);
      toast.success(`Publish complete — ${data.totals.inserted} new, ${data.totals.updated} updated`);
      await Promise.all([runDryRun(uploadedSnapshot), loadHistory()]);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPublishing(false);
    }
  };

  const clearSnapshot = () => {
    setUploadedSnapshot(null);
    setUploadedFileName("");
    setDiff(null);
    setLastReport(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const hasChanges = diff && (diff.totals.new + diff.totals.updated) > 0;
  const configured = config?.configured;

  return (
    <div className="p-8 max-w-6xl" data-testid="admin-content-sync">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <UploadCloud className="w-6 h-6 text-blue-600" />
            Publish to Production
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Promote CMS content (articles, drafts, redirects, topics, SEO metadata) from preview to
            production. Operational data — loans, applications, attorneys, firms, agreements, emails
            — is never touched.
          </p>
        </div>
        <div
          className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${
            environment === "PRODUCTION"
              ? "bg-red-50 text-red-700 border-red-200"
              : environment === "PREVIEW"
              ? "bg-slate-100 text-slate-700 border-slate-300"
              : "bg-amber-50 text-amber-800 border-amber-200"
          }`}
          data-testid="env-badge"
        >
          You are viewing: {environment}
        </div>
      </div>

      {/* Config status */}
      {configLoading ? (
        <div className="text-sm text-slate-500 py-6">Loading configuration…</div>
      ) : !configured ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 mb-6" data-testid="sync-not-configured">
          <div className="flex items-start gap-3">
            <ShieldAlert className="w-5 h-5 text-amber-700 mt-0.5" />
            <div className="text-sm text-amber-900">
              <div className="font-medium mb-1">Content sync is not yet configured on this environment</div>
              <p><code>CONTENT_SYNC_SECRET</code> is not set. Add the same random string as a Custom Key in the Emergent Secrets panel on this environment.</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 mb-6 flex items-center justify-between" data-testid="sync-configured">
          <div className="flex items-center gap-3">
            <ShieldCheck className="w-5 h-5 text-emerald-700" />
            <div className="text-sm text-emerald-900">
              <span className="font-medium">Configured.</span>{" "}
              Snapshot signing is active. Snapshots are valid for {config.snapshot_max_age_hours}h after generation.
            </div>
          </div>
          <StatusPill tone="green"><CheckCircle2 className="w-3 h-3" /> Ready</StatusPill>
        </div>
      )}

      {/* Scope card */}
      <div className="rounded-xl border border-slate-200 bg-white p-5 mb-6">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 mt-0.5" />
          <div className="text-sm text-slate-700">
            <div className="font-medium text-slate-900 mb-1">What gets synchronized</div>
            <p>
              <span className="font-medium text-emerald-700">Included:</span>{" "}
              blog posts (published, drafts, archived — status preserved), redirects, editorial topics,
              SEO metadata (meta descriptions, canonical URLs, Open Graph, hero images).
            </p>
            <p className="mt-1">
              <span className="font-medium text-red-700">Never touched:</span>{" "}
              loans, loan events, applications, agreements, agreement snapshots, attorneys, firms,
              firm users, payoff statements, email history, contact submissions.
            </p>
          </div>
        </div>
      </div>

      {/* ============ PREVIEW / SOURCE side ============ */}
      {environment !== "PRODUCTION" && configured && (
        <div className="rounded-xl border border-slate-200 bg-white p-6 mb-6" data-testid="download-snapshot-card">
          <div className="flex items-start justify-between mb-3">
            <div>
              <div className="flex items-center gap-2 text-slate-900 font-semibold">
                <FileJson className="w-5 h-5 text-blue-600" />
                Step 1 · Download a signed snapshot from this environment
              </div>
              <p className="text-sm text-slate-600 mt-1 max-w-xl">
                Exports every CMS record on this environment, cryptographically signed with your shared
                secret. Download the file, then upload it on the production admin page.
              </p>
            </div>
            <Button onClick={downloadSnapshot} disabled={downloading} data-testid="download-snapshot-btn">
              {downloading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              {downloading ? "Building snapshot…" : "Download Snapshot"}
            </Button>
          </div>
          <div className="text-xs text-slate-500 mt-2">
            After download, open{" "}
            <code className="bg-slate-100 px-1.5 py-0.5 rounded">https://instabridgefunding.com/admin/content-sync</code>{" "}
            and upload the file there.
          </div>
        </div>
      )}

      {/* ============ PRODUCTION / TARGET side ============ */}
      {environment !== "PREVIEW" && configured && (
        <>
          <div className="rounded-xl border border-slate-200 bg-white p-6 mb-6" data-testid="upload-snapshot-card">
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="flex items-center gap-2 text-slate-900 font-semibold">
                  <span className="inline-flex w-6 h-6 rounded-full bg-slate-900 text-white text-xs items-center justify-center">1</span>
                  Load Snapshot
                </div>
                <p className="text-sm text-slate-600 mt-1">Upload the signed JSON snapshot you downloaded from preview.</p>
              </div>
              {uploadedSnapshot && (
                <Button variant="outline" onClick={clearSnapshot} data-testid="clear-snapshot-btn">Clear</Button>
              )}
            </div>

            {!uploadedSnapshot ? (
              <label className="block cursor-pointer border-2 border-dashed border-slate-300 hover:border-blue-400 rounded-lg p-8 text-center transition-colors" data-testid="upload-dropzone">
                <UploadCloud className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                <div className="text-sm font-medium text-slate-900">Click to select snapshot file</div>
                <div className="text-xs text-slate-500 mt-1">e.g. instabridge-cms-snapshot-2026-02-17T…json</div>
                <input
                  ref={fileRef}
                  type="file"
                  accept="application/json,.json"
                  className="hidden"
                  onChange={handleFile}
                  data-testid="upload-file-input"
                />
              </label>
            ) : (
              <div className="rounded-lg bg-slate-50 border border-slate-200 p-4">
                <div className="flex items-center gap-3">
                  <FileJson className="w-5 h-5 text-blue-600" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-900 truncate">{uploadedFileName}</div>
                    <div className="text-xs text-slate-500">
                      Generated {new Date(uploadedSnapshot.generated_at).toLocaleString()} by {uploadedSnapshot.generated_by || "unknown"}
                      {" · "}
                      posts: {uploadedSnapshot.counts?.blog_posts ?? "?"} · redirects: {uploadedSnapshot.counts?.blog_redirects ?? "?"} · topics: {uploadedSnapshot.counts?.blog_topics ?? "?"}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Diff */}
          {uploadedSnapshot && (
            <div className="rounded-xl border border-slate-200 bg-white p-6 mb-6" data-testid="preview-changes-card">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2 text-slate-900 font-semibold">
                    <span className="inline-flex w-6 h-6 rounded-full bg-slate-900 text-white text-xs items-center justify-center">2</span>
                    Preview Changes
                  </div>
                  <p className="text-sm text-slate-600 mt-1">Signature verified. Diff below reflects exactly what will be written if you publish.</p>
                </div>
                <Button
                  onClick={() => runDryRun(uploadedSnapshot)}
                  disabled={diffLoading}
                  variant="outline"
                  data-testid="rerun-diff-btn"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${diffLoading ? "animate-spin" : ""}`} />
                  {diffLoading ? "Comparing…" : "Refresh Diff"}
                </Button>
              </div>

              {diff && (
                <div>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                    <SummaryStat label="New" value={diff.totals.new} tone={diff.totals.new > 0 ? "blue" : "slate"} />
                    <SummaryStat label="Updated" value={diff.totals.updated} tone={diff.totals.updated > 0 ? "amber" : "slate"} />
                    <SummaryStat label="SEO Changes" value={diff.totals.seo_changes} tone="slate" />
                    <SummaryStat label="Status Changes" value={diff.totals.status_changes} tone="slate" />
                    <SummaryStat label="Unchanged" value={diff.totals.unchanged} tone="slate" />
                  </div>

                  <div className="space-y-3">
                    {Object.entries(diff.collections).map(([coll, info]) => (
                      <div key={coll} className="rounded-lg border border-slate-200 p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="font-medium text-slate-900">{info.label || COLL_LABELS[coll] || coll}</div>
                          <div className="flex gap-2">
                            {info.new_count > 0 && <StatusPill tone="blue">+{info.new_count} new</StatusPill>}
                            {info.updated_count > 0 && <StatusPill tone="amber">{info.updated_count} updated</StatusPill>}
                            {info.new_count === 0 && info.updated_count === 0 && <StatusPill tone="green">In sync</StatusPill>}
                          </div>
                        </div>
                        {coll === "blog_posts" && (info.new_count > 0 || info.updated_count > 0) && (
                          <div className="text-xs text-slate-600 mb-2 flex flex-wrap gap-3">
                            {Object.entries(info.by_status_new || {}).map(([s, n]) => (
                              <span key={"n" + s}>New {s}: <strong>{n}</strong></span>
                            ))}
                            {Object.entries(info.by_status_updated || {}).map(([s, n]) => (
                              <span key={"u" + s}>Updated {s}: <strong>{n}</strong></span>
                            ))}
                          </div>
                        )}
                        {(info.sample_new?.length > 0 || info.sample_updated?.length > 0) && (
                          <div className="text-xs text-slate-500 space-y-1">
                            {info.sample_new?.length > 0 && (
                              <div><span className="text-blue-700 font-medium">Sample new:</span>{" "}
                                {info.sample_new.join(", ")}{info.new_count > info.sample_new.length ? " …" : ""}
                              </div>
                            )}
                            {info.sample_updated?.length > 0 && (
                              <div><span className="text-amber-700 font-medium">Sample updated:</span>{" "}
                                {info.sample_updated.join(", ")}{info.updated_count > info.sample_updated.length ? " …" : ""}
                              </div>
                            )}
                          </div>
                        )}
                        {info.orphans_count > 0 && (
                          <div className="mt-2 text-xs text-slate-500">
                            <span className="text-slate-600 font-medium">Note:</span> {info.orphans_count} local records
                            aren&apos;t present in the snapshot. They will <strong>not</strong> be deleted (sync never removes data).
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Publish */}
          {uploadedSnapshot && diff && (
            <div className="rounded-xl border border-slate-200 bg-white p-6 mb-6" data-testid="publish-card">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 text-slate-900 font-semibold">
                    <span className="inline-flex w-6 h-6 rounded-full bg-slate-900 text-white text-xs items-center justify-center">3</span>
                    Publish to Production
                  </div>
                  <p className="text-sm text-slate-600 mt-1">
                    {hasChanges
                      ? `Ready to promote ${diff.totals.new} new + ${diff.totals.updated} updated records. Only CMS collections will be modified.`
                      : "Everything in the snapshot is already in sync. Nothing to publish."}
                  </p>
                </div>
                <Button
                  onClick={() => setConfirmOpen(true)}
                  disabled={publishing || !hasChanges}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="publish-btn"
                >
                  {publishing ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Rocket className="w-4 h-4 mr-2" />}
                  {publishing ? "Publishing…" : "Publish to Production"}
                </Button>
              </div>
            </div>
          )}

          {/* Completion */}
          {lastReport && (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-6 mb-6" data-testid="publish-report">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-700 mt-0.5" />
                <div className="flex-1">
                  <div className="font-semibold text-emerald-900">Publish complete</div>
                  <div className="text-xs text-emerald-800 mt-0.5">
                    {lastReport.sync_id} · {new Date(lastReport.started_at).toLocaleString()} · by {lastReport.actor_email}
                  </div>
                  <div className="grid grid-cols-4 gap-3 mt-4">
                    <SummaryStat label="Inserted" value={lastReport.totals.inserted} tone="green" />
                    <SummaryStat label="Updated" value={lastReport.totals.updated} tone="amber" />
                    <SummaryStat label="Unchanged" value={lastReport.totals.unchanged} tone="slate" />
                    <SummaryStat label="Errors" value={lastReport.totals.errors} tone={lastReport.totals.errors > 0 ? "red" : "slate"} />
                  </div>
                  <div className="mt-4 space-y-2">
                    {Object.entries(lastReport.collections).map(([coll, info]) => (
                      <div key={coll} className="text-sm bg-white/60 rounded px-3 py-2 flex justify-between items-center">
                        <span className="font-medium text-slate-800">{info.label}</span>
                        <span className="text-slate-600 text-xs">
                          +{info.inserted} new · ~{info.updated} updated · ={info.unchanged} unchanged
                          {info.errors.length > 0 && <span className="text-red-700 ml-2">· {info.errors.length} errors</span>}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* History (both sides) */}
      {history.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="sync-history">
          <div className="flex items-center gap-2 text-slate-900 font-semibold mb-4">
            <Clock className="w-5 h-5 text-slate-500" />
            Recent syncs
          </div>
          <div className="divide-y divide-slate-100">
            {history.map((h) => (
              <div key={h.sync_id} className="py-3 flex items-center justify-between text-sm">
                <div>
                  <div className="font-medium text-slate-800">{new Date(h.started_at).toLocaleString()}</div>
                  <div className="text-xs text-slate-500">{h.actor_email} · {h.sync_id} · via {h.source || "pull"}</div>
                </div>
                <div className="text-xs text-slate-600 flex gap-3">
                  <span>+{h.totals.inserted} new</span>
                  <span>~{h.totals.updated} updated</span>
                  <span>={h.totals.unchanged} unchanged</span>
                  {h.totals.errors > 0 && <span className="text-red-700">{h.totals.errors} errors</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Confirm modal */}
      {confirmOpen && (
        <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4" data-testid="confirm-modal">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0" />
              <div>
                <div className="font-semibold text-slate-900 text-lg">Publish to production?</div>
                <p className="text-sm text-slate-600 mt-2">
                  About to promote <strong>{diff.totals.new} new</strong> and{" "}
                  <strong>{diff.totals.updated} updated</strong> CMS records. Operational data
                  (loans, agreements, attorneys, firms) will not be touched.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="outline" onClick={() => setConfirmOpen(false)} data-testid="confirm-cancel">Cancel</Button>
              <Button onClick={runPublish} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="confirm-publish">
                Yes, publish now
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
